import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { FaChartBar } from "react-icons/fa";
import AxiosInstance, { TokenService } from "../../../../services/Api";
import academicStyles from "../../../../pages/schoolCockpit/master/academicConfiguration/Academiclist.module.css";
import dashStyles from "../../aidashboard/AiDashboard.module.css";
import SchoolFeesTrend from "../../../../components/Chart/SchoolFeesDashboard/SchoolFeesTrend/SchoolFeeStatusCards";
import FeeSummaryTrendCard from "../../../../components/Chart/SchoolFeesDashboard/feeSummaryTrendCard/FeeSummaryTrendCard";
import PaymentModesDistribution from "../../../../components/Chart/SchoolFeesDashboard/paymentModesDistribution/PaymentModesDistribution";
import ClassWiseRecoveryBar from "../../../../components/Chart/SchoolFeesDashboard/classWiseRecoveryBar/ClassWiseRecoveryBar";
import feeDashboardStyles from "./FeeDashboard.module.css";

/* ============================================================
 * Fees-trend endpoints. Collected / Not Paid / Pending all share
 * the SAME payload key `academic_year` (NOT academic_year_id) and
 * return `data` as a ONE-element array.
 * ⚠ Not-Paid path is spelled "NotPaiAmount" on the server — match it.
 * ============================================================ */
const COLLECTED_URL = "schoolFees/hm/collectedAmount";
const NOT_PAID_URL = "schoolFees/hm/NotPaiAmount";
const PENDING_PAYMENT_URL = "schoolFees/hm/pendingPayment";

/* Summary line-chart endpoints. These use `academic_year_id`. */
const TOTAL_DEMAND_URL = "schoolFees/feeDashboard/totalDemand";
const TOTAL_DISCOUNT_URL = "schoolFees/feeDashboard/totalDiscount";

/* Class-wise + section-wise recovery share ONE endpoint.
   Both use `academic_id` (NOT academic_year_id):
     level 1 (all classes) → class_id: ""
     level 2 (one class's sections) → class_id: <that class's id, e.g. 66> */
const CLASS_RECOVERY_URL = "schoolFees/hm/classWiseRecovery";

/* Payment-mode distribution. `academic_id` payload; returns an OBJECT. */
const PAYMENT_MODE_URL = "schoolFees/hm/paymentMode";

/* Where a section-bar click lands. Matches the Collect Fees list route. */
const COLLECT_FEES_ROUTE = "/user/listCollectFees";

/* ── Stacked segments for the recovery bars ── */
const RECOVERY_SEGMENTS = [
  { key: "paid", darkVar: "--green-dark", lightVar: "--green-light" },
  { key: "partly", darkVar: "--orange-dark", lightVar: "--orange-light" },
  { key: "notPaid", darkVar: "--red-dark", lightVar: "--red-light" },
];
const RECOVERY_SEGMENT_LABELS = {
  paid: "Paid",
  partly: "Partly paid",
  notPaid: "Not paid",
};

/* ============================================================
 * ₹ formatter — raw numeric strings ("82800.00") → full amount,
 * Indian grouping, no rounding.
 * ============================================================ */
const formatINR = (raw) => {
  const n = Number(raw);
  if (!Number.isFinite(n)) return "₹0";
  const hasPaise = Math.round(n * 100) % 100 !== 0;
  return `₹${n.toLocaleString("en-IN", {
    minimumFractionDigits: hasPaise ? 2 : 0,
    maximumFractionDigits: 2,
  })}`;
};

/* Pull the single object out of an API's one-element `data` array. */
const firstRow = (payload) =>
  Array.isArray(payload) ? payload[0] || null : payload || null;

const mapCollected = (row) => {
  if (!row) return null;
  return {
    total_display: formatINR(row.total_collected),
    students_count: Number(row.students_paid) || 0,
    on_time: Number(row.on_time_students) || 0,
    overdue: Number(row.overdue_students) || 0,
    regular_display: formatINR(row.regular_fees),
    regular_percent: Number(row.regular_pct) || 0,
    admission_display: formatINR(row.admission_fees),
    admission_percent: Number(row.admission_pct) || 0,
  };
};

const mapNotPaid = (row) => {
  if (!row) return null;
  return {
    total_display: formatINR(row.total_pending),
    students_count: Number(row.students_pending) || 0,
    within_due: Number(row.within_due_students) || 0,
    overdue: Number(row.overdue_students) || 0,
    regular_display: formatINR(row.regular_fees),
    regular_percent: Number(row.regular_pct) || 0,
    admission_display: formatINR(row.admission_fees),
    admission_percent: Number(row.admission_pct) || 0,
  };
};

/* Pending Approval card. Field names mirror the Collected/Not-Paid
   endpoints; several fallbacks are read in case this one differs.
   ⚠ Correct these once the real response is known. */
/* Pending Approval card mapping with correct API key alignment */
const mapPending = (row) => {
  if (!row) return null;
  return {
    total_display: formatINR(
      row.total_pending_approval ??
      row.total_pending ??
      row.total_amount ??
      row.total ??
      0
    ),
    students_count: Number(
      row.pending_students ??
      row.pending_payments ??
      row.pending_approval_students ??
      row.students_pending ??
      0
    ) || 0,
    on_time: Number(
      row.on_time_count ??
      row.on_time_students ??
      row.on_time ??
      0
    ) || 0,
    overdue: Number(
      row.overdue_count ??
      row.overdue_students ??
      row.overdue ??
      0
    ) || 0,
    regular_display: formatINR(row.regular_fees),
    regular_percent: Number(row.regular_pct) || 0,
    admission_display: formatINR(row.admission_fees),
    admission_percent: Number(row.admission_pct) || 0,
  };
};

/* One mapper for BOTH recovery levels. The API nests each segment:
     paid:       { count, percentage, amount_collected }
     partly_paid:{ count, percentage, amount_collected, pending_amount }
     not_paid:   { count, percentage, pending_amount }
   Paid shows the amount collected; partly/not-paid show pending. */
const mapRecoveryRow = (row) => {
  const paid = row.paid || {};
  const partly = row.partly_paid || {};
  const notPaid = row.not_paid || {};
  return {
    // section rows carry BOTH section_name ("A") and class_name ("VI");
    // section_name must win, or every section bar shows the class name.
    // At class level section_name is absent → falls back to class_name.
    label: row.section_name ?? row.class_name ?? row.label ?? "",
    class_id: row.class_id ?? row.classId ?? null,
    section_id: row.section_id ?? row.sectionId ?? null,
    totalStudents: Number(row.total_students) || 0,

    paid: Number(paid.percentage) || 0,
    partly: Number(partly.percentage) || 0,
    notPaid: Number(notPaid.percentage) || 0,

    paidStudents: Number(paid.count) || 0,
    paidAmountDisplay: formatINR(paid.amount_collected),
    partlyStudents: Number(partly.count) || 0,
    partlyAmountDisplay: formatINR(partly.pending_amount),
    notPaidStudents: Number(notPaid.count) || 0,
    notPaidAmountDisplay: formatINR(notPaid.pending_amount),
  };
};

/* Payment-mode distribution → the [{ key, label, percent }] shape
   PaymentModesDistribution expects.
   { data: { online_percentage, cash_percentage, ... } } */
const mapPaymentModes = (row) => {
  if (!row) return [];
  return [
    {
      key: "online",
      label: "Online Payment",
      percent: Number(row.online_percentage) || 0,
    },
    {
      key: "cash",
      label: "Cash",
      percent: Number(row.cash_percentage) || 0,
    },
  ];
};

/* ── Summary-chart mappers (Total Demand / Discount) ──
   Both endpoints return `data` as a plain OBJECT with `chart_data`.
   Demand points:   { year, total_amount }
   Discount points: { year, total_discount, student_count }
   The value key differs, so each has its own mapper. */

/* Total Demand → the shape FeeSummaryTrendCard expects.
   { data: { total_demand, total_demand_formatted, total_students,
             chart_data: [{ year, total_amount }] } } */
/* Total Demand mapper */
const mapTotalDemand = (row) => {
  if (!row) return null;
  const chart = Array.isArray(row.chart_data) ? row.chart_data : [];
  return {
    // full amount (₹81,000), NOT the API's abbreviated total_demand_formatted
    total_display: formatINR(row.total_demand),
    students_count: Number(row.total_students) || 0,
    series: chart.map((p) => {
      // FORCE strict numeric float conversion
      const value = parseFloat(p.total_amount) || 0;
      return {
        year: p.year ?? "",
        value: value, // MUST BE A NUMBER
        displayValue: formatINR(value),
      };
    }),
  };
};

/* Total Discount → same card shape, plus a secondary series
   (student count) that the discount tooltip shows.
   { data: { total_discount, total_discount_formatted, total_students,
             chart_data: [{ year, total_discount, student_count }] } } */
const mapTotalDiscount = (row) => {
  if (!row) return null;
  const chart = Array.isArray(row.chart_data) ? row.chart_data : [];
  return {
    // full amount (₹2,400), NOT the API's abbreviated total_discount_formatted
    total_display: formatINR(row.total_discount),
    students_count: Number(row.total_students) || 0,
    series: chart.map((p) => {
      // FORCE strict numeric float conversion
      const value = parseFloat(p.total_discount) || 0;
      return {
        year: p.year ?? "",
        value: value, // MUST BE A NUMBER
        displayValue: formatINR(value),
        secondaryValue: Number(p.student_count) || 0,
      };
    }),
  };
};

const recoveryTooltipLines = (rows, dataIndex) => {
  const r = rows?.[dataIndex];
  if (!r) return null;
  return [
    `Students Paid : ${r.paidStudents}`,
    `Amount Collected : ${r.paidAmountDisplay}`,
    `Students Partly paid : ${r.partlyStudents}`,
    `Pending Amount : ${r.partlyAmountDisplay}`,
    `Students Not paid : ${r.notPaidStudents}`,
    `Pending Amount : ${r.notPaidAmountDisplay}`,
  ];
};

const FeeDashboard = () => {
  const navigate = useNavigate();
  const { translations } = useSelector((state) => state.language);
  const t = translations?.labels || {};

  const user = TokenService.getUser();
  const clientData = user?.client_data?.[0] || {};
  const school_id = clientData?.school_id || "";
  const academic_year_id =
    clientData?.academic_year_id || clientData?.academic_id || "";

  const [trendData, setTrendData] = useState(null);
  const [trendLoading, setTrendLoading] = useState(false);
  const [trendError, setTrendError] = useState(null);
  const trendAbortRef = useRef(null);

  const [summaryCharts, setSummaryCharts] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [summaryError, setSummaryError] = useState(null);
  const summaryAbortRef = useRef(null);

  /* ── Class-wise recovery (drill level 1) ── */
  const [recoveryData, setRecoveryData] = useState(null);
  const [recoveryLoading, setRecoveryLoading] = useState(false);
  const [recoveryError, setRecoveryError] = useState(null);
  const recoveryAbortRef = useRef(null);

  /* ── Section-wise recovery (drill level 2) ── */
  const [recoveryLevel, setRecoveryLevel] = useState("class"); // "class" | "section"
  const [selectedClass, setSelectedClass] = useState(null);
  const [sectionData, setSectionData] = useState([]);
  const [sectionLoading, setSectionLoading] = useState(false);
  const [sectionError, setSectionError] = useState(null);
  const sectionAbortRef = useRef(null);

  const [paymentModes, setPaymentModes] = useState(null);
  const [paymentModesLoading, setPaymentModesLoading] = useState(false);
  const [paymentModesError, setPaymentModesError] = useState(null);
  const paymentModesAbortRef = useRef(null);

  /* ===== Fetch: School Fees Trend (Collected + Not Paid + Pending) =====
     All three share the `academic_year` payload; fired together with
     allSettled so one failure doesn't blank the others. */
  const fetchFeesTrend = useCallback(async () => {
    if (!school_id || !academic_year_id) return;

    if (trendAbortRef.current) trendAbortRef.current.abort();
    const controller = new AbortController();
    trendAbortRef.current = controller;

    setTrendLoading(true);
    setTrendError(null);

    const body = {
      school_id: Number(school_id),
      academic_year: Number(academic_year_id),
    };
    const cfg = { signal: controller.signal };

    try {
      const [collectedRes, notPaidRes, pendingRes] = await Promise.allSettled([
        AxiosInstance.post(COLLECTED_URL, body, cfg),
        AxiosInstance.post(NOT_PAID_URL, body, cfg),
        AxiosInstance.post(PENDING_PAYMENT_URL, body, cfg),
      ]);

      if (controller.signal.aborted) return;

      let collected = null;
      let notPaid = null;
      let pending = null;
      const failures = [];

      if (collectedRes.status === "fulfilled") {
        const d = collectedRes.value?.data;
        if (d?.status || d?.success) collected = mapCollected(firstRow(d.data));
        else failures.push(d?.message);
      } else {
        failures.push(collectedRes.reason?.message);
      }

      if (notPaidRes.status === "fulfilled") {
        const d = notPaidRes.value?.data;
        if (d?.status || d?.success) notPaid = mapNotPaid(firstRow(d.data));
        else failures.push(d?.message);
      } else {
        failures.push(notPaidRes.reason?.message);
      }

      if (pendingRes.status === "fulfilled") {
        const d = pendingRes.value?.data;
        if (d?.status || d?.success) pending = mapPending(firstRow(d.data));
        else failures.push(d?.message);
      } else {
        failures.push(pendingRes.reason?.message);
      }

      setTrendData({
        collected,
        not_paid: notPaid,
        pending_approval: pending,
      });

      if (!collected && !notPaid && !pending) {
        setTrendError(
          failures.find(Boolean) || t.somethingWrong || "Something went wrong",
        );
      }
    } catch (error) {
      if (error.name !== "AbortError" && error.name !== "CanceledError") {
        console.error("Error fetching fees trend:", error);
        setTrendError(t.somethingWrong || "Something went wrong");
        setTrendData(null);
      }
    } finally {
      if (!controller.signal.aborted) setTrendLoading(false);
    }
  }, [school_id, academic_year_id, t.somethingWrong]);

  /* ===== Fetch: Summary Charts (Total Demand + Total Discount) =====
     Two endpoints, `academic_year_id` payload, fired together. */
  const fetchFeeSummaryCharts = useCallback(async () => {
    if (!school_id || !academic_year_id) return;

    if (summaryAbortRef.current) summaryAbortRef.current.abort();
    const controller = new AbortController();
    summaryAbortRef.current = controller;

    setSummaryLoading(true);
    setSummaryError(null);

    const body = {
      school_id: Number(school_id),
      academic_year_id: Number(academic_year_id),
    };
    const cfg = { signal: controller.signal };

    try {
      const [demandRes, discountRes] = await Promise.allSettled([
        AxiosInstance.post(TOTAL_DEMAND_URL, body, cfg),
        AxiosInstance.post(TOTAL_DISCOUNT_URL, body, cfg),
      ]);

      if (controller.signal.aborted) return;

      let totalDemand = null;
      let discount = null;
      const failures = [];

      if (demandRes.status === "fulfilled") {
        const d = demandRes.value?.data;
        // `data` is a plain object here, not an array
        if (d?.status || d?.success) totalDemand = mapTotalDemand(d.data);
        else failures.push(d?.message);
      } else {
        failures.push(demandRes.reason?.message);
      }

      if (discountRes.status === "fulfilled") {
        const d = discountRes.value?.data;
        if (d?.status || d?.success) discount = mapTotalDiscount(d.data);
        else failures.push(d?.message);
      } else {
        failures.push(discountRes.reason?.message);
      }

      setSummaryCharts({
        total_demand: totalDemand,
        discount,
      });

      if (!totalDemand && !discount) {
        setSummaryError(
          failures.find(Boolean) || t.somethingWrong || "Something went wrong",
        );
      }
    } catch (error) {
      if (error.name !== "AbortError" && error.name !== "CanceledError") {
        console.error("Error fetching fee summary charts:", error);
        setSummaryError(t.somethingWrong || "Something went wrong");
        setSummaryCharts(null);
      }
    } finally {
      if (!controller.signal.aborted) setSummaryLoading(false);
    }
  }, [school_id, academic_year_id, t.somethingWrong]);

  /* ===== Fetch: Class Wise Recovery (drill level 1) ===== */
  const fetchClassWiseRecovery = useCallback(async () => {
    if (!school_id || !academic_year_id) return;

    if (recoveryAbortRef.current) recoveryAbortRef.current.abort();
    const controller = new AbortController();
    recoveryAbortRef.current = controller;

    setRecoveryLoading(true);
    setRecoveryError(null);

    try {
      const response = await AxiosInstance.post(
        CLASS_RECOVERY_URL,
        {
          school_id: Number(school_id),
          academic_id: Number(academic_year_id),
          class_id: "",
        },
        { signal: controller.signal },
      );

      const ok = response?.data?.status || response?.data?.success;
      const result = response?.data?.data;

      if (ok && Array.isArray(result)) {
        setRecoveryData(result.map(mapRecoveryRow));
      } else {
        setRecoveryError(
          response?.data?.message || t.somethingWrong || "Something went wrong",
        );
        setRecoveryData(null);
      }
    } catch (error) {
      if (error.name !== "AbortError" && error.name !== "CanceledError") {
        console.error("Error fetching class wise recovery:", error);
        setRecoveryError(t.somethingWrong || "Something went wrong");
        setRecoveryData(null);
      }
    } finally {
      setRecoveryLoading(false);
    }
  }, [school_id, academic_year_id, t.somethingWrong]);

  /* ===== Fetch: Section Wise Recovery (drill level 2) ===== */
  const fetchSectionWiseRecovery = useCallback(
    async (classRow) => {
      if (!school_id || !academic_year_id || !classRow) return;

      if (sectionAbortRef.current) sectionAbortRef.current.abort();
      const controller = new AbortController();
      sectionAbortRef.current = controller;

      setSectionLoading(true);
      setSectionError(null);

      try {
        const response = await AxiosInstance.post(
          CLASS_RECOVERY_URL,
          {
            school_id: Number(school_id),
            academic_id: Number(academic_year_id),
            class_id: classRow.class_id ?? "",
          },
          { signal: controller.signal },
        );

        if (controller.signal.aborted) return;

        const ok = response?.data?.status || response?.data?.success;
        const result = response?.data?.data;

        if (ok && Array.isArray(result)) {
          setSectionData(result.map(mapRecoveryRow));
        } else {
          setSectionData([]);
          setSectionError(
            response?.data?.message ||
            t.somethingWrong ||
            "Something went wrong",
          );
        }
      } catch (error) {
        if (error.name !== "AbortError" && error.name !== "CanceledError") {
          console.error("Error fetching section wise recovery:", error);
          setSectionData([]);
          setSectionError(t.somethingWrong || "Something went wrong");
        }
      } finally {
        if (!controller.signal.aborted) setSectionLoading(false);
      }
    },
    [school_id, academic_year_id, t.somethingWrong],
  );

  /* ===== Fetch: Payment Modes Distribution ===== */
  const fetchPaymentModesDistribution = useCallback(async () => {
    if (!school_id || !academic_year_id) return;

    if (paymentModesAbortRef.current) paymentModesAbortRef.current.abort();
    const controller = new AbortController();
    paymentModesAbortRef.current = controller;

    setPaymentModesLoading(true);
    setPaymentModesError(null);

    try {
      const response = await AxiosInstance.post(
        PAYMENT_MODE_URL,
        {
          school_id: Number(school_id),
          academic_id: Number(academic_year_id),
        },
        { signal: controller.signal },
      );

      const ok = response?.data?.status || response?.data?.success;
      const result = response?.data?.data; // OBJECT, not an array

      if (ok && result) {
        setPaymentModes(mapPaymentModes(result));
      } else {
        setPaymentModesError(
          response?.data?.message || t.somethingWrong || "Something went wrong",
        );
        setPaymentModes(null);
      }
    } catch (error) {
      if (error.name !== "AbortError" && error.name !== "CanceledError") {
        console.error("Error fetching payment modes distribution:", error);
        setPaymentModesError(t.somethingWrong || "Something went wrong");
        setPaymentModes(null);
      }
    } finally {
      setPaymentModesLoading(false);
    }
  }, [school_id, academic_year_id, t.somethingWrong]);

  useEffect(() => {
    fetchFeesTrend();
    return () => trendAbortRef.current?.abort();
  }, [fetchFeesTrend]);

  useEffect(() => {
    fetchFeeSummaryCharts();
    return () => summaryAbortRef.current?.abort();
  }, [fetchFeeSummaryCharts]);

  useEffect(() => {
    fetchClassWiseRecovery();
    return () => recoveryAbortRef.current?.abort();
  }, [fetchClassWiseRecovery]);

  useEffect(() => {
    fetchPaymentModesDistribution();
    return () => paymentModesAbortRef.current?.abort();
  }, [fetchPaymentModesDistribution]);

  useEffect(() => () => sectionAbortRef.current?.abort(), []);

  /* ===== Recovery drill-down handlers ===== */
  const handleRecoveryBarClick = useCallback(
    (row, _idx, lvl) => {
      if (!row) return;

      if (lvl === "class") {
        setSelectedClass(row);
        setRecoveryLevel("section");
        setSectionData([]);
        fetchSectionWiseRecovery(row);
        return;
      }

      if (lvl === "section") {
        navigate(COLLECT_FEES_ROUTE, {
          state: {
            class_id: selectedClass?.class_id ?? null,
            class_name: selectedClass?.label ?? null,
            section_id: row?.section_id ?? null,
            section_name: row?.label ?? null,
          },
        });
      }
    },
    [fetchSectionWiseRecovery, navigate, selectedClass],
  );

  const handleRecoveryBack = useCallback(() => {
    sectionAbortRef.current?.abort();
    setRecoveryLevel("class");
    setSelectedClass(null);
    setSectionData([]);
    setSectionError(null);
  }, []);

  const activeRecoveryRows =
    recoveryLevel === "section" ? sectionData : recoveryData || [];
  const activeRecoveryLoading =
    recoveryLevel === "section" ? sectionLoading : recoveryLoading;
  const activeRecoveryError =
    recoveryLevel === "section" ? sectionError : recoveryError;

  /* ===== Transform: API response → cards array ===== */
  const feesTrendCards = useMemo(() => {
    if (!trendData) return [];

    const collected = trendData.collected || {};
    const notPaid = trendData.not_paid || {};
    const pending = trendData.pending_approval || {};

    return [
      {
        id: "collected",
        variant: "success",
        amount: collected.total_display ?? "₹0",
        statusLabel: t.collected || "Collected",
        studentsText: `${collected.students_count ?? 0} ${t.studentsPaid || "students paid"}`,
        stats: [
          { label: t.onTime || "On Time", value: collected.on_time ?? 0 },
          { label: t.overdue || "Overdue", value: collected.overdue ?? 0 },
        ],
        breakdown: {
          left: {
            label: t.regularFees || "Regular Fees",
            amount: collected.regular_display ?? "₹0",
            percent: collected.regular_percent ?? 0,
          },
          right: {
            label: t.admissionFees || "Admission Fees",
            amount: collected.admission_display ?? "₹0",
            percent: collected.admission_percent ?? 0,
          },
        },
      },
      {
        id: "not_paid",
        variant: "danger",
        amount: notPaid.total_display ?? "₹0",
        statusLabel: t.notPaid || "Not Paid",
        studentsText: `${notPaid.students_count ?? 0} ${t.studentsPending || "Students Pending"}`,
        stats: [
          { label: t.withinDue || "Within Due", value: notPaid.within_due ?? 0 },
          { label: t.overdue || "Overdue", value: notPaid.overdue ?? 0 },
        ],
        breakdown: {
          left: {
            label: t.regularFees || "Regular Fees",
            amount: notPaid.regular_display ?? "₹0",
            percent: notPaid.regular_percent ?? 0,
          },
          right: {
            label: t.admissionFees || "Admission Fees",
            amount: notPaid.admission_display ?? "₹0",
            percent: notPaid.admission_percent ?? 0,
          },
        },
      },
      {
        id: "pending_approval",
        variant: "warning",
        amount: pending.total_display ?? "₹0",
        statusLabel: t.pendingApproval || "Pending Approval",
        studentsText: `${pending.students_count ?? 0} ${t.pendingApprovalPayments || "Pending Approval Payments"}`,
        stats: [
          { label: t.onTime || "On Time", value: pending.on_time ?? 0 },
          { label: t.overdue || "Overdue", value: pending.overdue ?? 0 },
        ],
        breakdown: {
          left: {
            label: t.regularFees || "Regular Fees",
            amount: pending.regular_display ?? "₹0",
            percent: pending.regular_percent ?? 0,
          },
          right: {
            label: t.admissionFees || "Admission Fees",
            amount: pending.admission_display ?? "₹0",
            percent: pending.admission_percent ?? 0,
          },
        },
      },
    ];
  }, [trendData, t]);

  const totalDemand = summaryCharts?.total_demand;
  const totalDemandData = useMemo(
    () => totalDemand?.series || [],
    [totalDemand],
  );

  const discount = summaryCharts?.discount;
  const discountData = useMemo(() => discount?.series || [], [discount]);

  return (
    <div className={academicStyles.container}>
      <div className={`${dashStyles.row} ${feeDashboardStyles.section}`}>
        <div className={`${dashStyles.col} ${dashStyles.col12}`}>
          <div className={dashStyles.card}>
            <SchoolFeesTrend
              title={t.schoolFeesTrend || "School Fees Trend"}
              cards={feesTrendCards}
              loading={trendLoading}
              error={trendError}
            />
          </div>
        </div>
      </div>

      <div className={`${dashStyles.row} ${feeDashboardStyles.section}`}>
        <div className={`${dashStyles.col} ${dashStyles.col6}`}>
          <div className={dashStyles.card}>
            <FeeSummaryTrendCard
              variant="blue"
              title={t.totalDemand || "Total Demand"}
              amount={totalDemand?.total_display ?? "₹0"}
              subtitle={`${t.basedOn || "Based on"} ${totalDemand?.students_count ?? 0
                } ${t.students || "Students"}`}
              data={totalDemandData}
              loading={summaryLoading}
              error={summaryError}
            />
          </div>
        </div>

        <div className={`${dashStyles.col} ${dashStyles.col6}`}>
          <div className={dashStyles.card}>
            <FeeSummaryTrendCard
              variant="purple"
              title={t.discount || "Discount"}
              amount={discount?.total_display ?? "₹0"}
              subtitle={`${discount?.students_count ?? 0} ${t.studentsForThisYear || "students for this year"
                }`}
              secondaryLabel={t.studentsCount || "Students Count"}
              data={discountData}
              loading={summaryLoading}
              error={summaryError}
            />
          </div>
        </div>
      </div>

      <div className={`${dashStyles.row} ${feeDashboardStyles.section}`}>
        {/* Class → Section Wise Recovery. Class bars drill in; section
            bars navigate to the pre-filtered Collect Fees list. */}
        <div className={`${dashStyles.col} ${dashStyles.col12}`}>
          <div className={dashStyles.card}>
            <ClassWiseRecoveryBar
              icon={<FaChartBar />}
              /* Title swaps cleanly by level (matches the screenshots):
                 class → "Class Wise Recovery Bar"
                 section → "Section Wise Recovery Bar" */
              title={
                recoveryLevel === "section"
                  ? t.sectionWiseRecoveryBar || "Section Wise Recovery Bar"
                  : t.classWiseRecoveryBar || "Class Wise Recovery Bar"
              }
              stacked
              serverDrilldown
              serverDrillLevels={["class", "section"]}
              level={recoveryLevel}
              data={activeRecoveryRows}
              segments={RECOVERY_SEGMENTS}
              segmentLabels={RECOVERY_SEGMENT_LABELS}
              onBarClick={handleRecoveryBarClick}
              onBack={handleRecoveryBack}
              loading={activeRecoveryLoading}
              error={activeRecoveryError}
              maxY={100}
              stepSize={20}
              valueSuffix="%"
              /* empty → CustomBarChart won't append anything to the
                 already-correct section title above */
              sectionWiseSuffix=""
              /* Guidance now shown as a "Note:" in the chart's top-right
                 corner instead of the text below the bars. */
              info={
                recoveryLevel === "section"
                  ? "Click any section to open its collect-fees list"
                  : "Click any class to view section-wise breakdown"
              }
              tooltipAfterBody={(dataIndex) =>
                recoveryTooltipLines(activeRecoveryRows, dataIndex)
              }
            />
          </div>
        </div>
      </div>

      <div className={dashStyles.row}>
        <div className={`${dashStyles.col} ${dashStyles.col12}`}>
          <div className={dashStyles.card}>
            <PaymentModesDistribution
              title={t.paymentModesDistribution || "Payment Modes Distribution"}
              modes={paymentModes || []}
              loading={paymentModesLoading}
              error={paymentModesError}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default FeeDashboard;
